import React from 'react';
import { Lock, Crown, Sparkles, ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import useSubscription from '../hooks/useSubscription';
import AuthModal from './AuthModal';

const SubscriptionGate = ({ 
  feature, 
  children, 
  showPreview = false, 
  previewContent = null,
  className = "",
  title = "Premium Feature",
  description = null 
}) => {
  const { hasFeatureAccess, requiresUpgrade, getUpgradeMessage, isFreeUser } = useSubscription();

  // If user has access, show the content
  if (hasFeatureAccess(feature)) {
    return <>{children}</>;
  }

  // If showing preview, render limited version
  if (showPreview && previewContent) {
    return (
      <div className={`relative ${className}`}>
        {previewContent}
        
        {/* Preview overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
        
        {/* Upgrade prompt */}
        <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto">
          <Card className="bg-white/95 backdrop-blur-sm border-amber-200">
            <CardContent className="p-4 text-center">
              <Crown className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-900 mb-2">
                {getUpgradeMessage(feature)}
              </p>
              <Button 
                size="sm" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => window.location.href = '/subscription'}
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Upgrade Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Full subscription gate
  return (
    <Card className={`bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 ${className}`}>
      <CardHeader className="text-center pb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-white" />
        </div>
        
        <Badge className="bg-amber-100 text-amber-800 mb-2">
          👑 Crown Feature
        </Badge>
        
        <CardTitle className="text-xl font-bold text-gray-900">
          {title}
        </CardTitle>
        
        {description && (
          <p className="text-sm text-gray-600 mt-2">
            {description}
          </p>
        )}
      </CardHeader>

      <CardContent className="text-center space-y-4">
        <div className="p-4 bg-white rounded-lg border border-amber-200">
          <Lock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
          <p className="text-sm text-gray-700 font-medium">
            {getUpgradeMessage(feature)}
          </p>
        </div>

        <div className="space-y-2">
          {isFreeUser ? (
            <>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700"
                onClick={() => window.location.href = '/subscription'}
              >
                <Crown className="w-4 h-4 mr-2" />
                Choose Your Plan
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-gray-500">
                The Seekers Path: $9.99/month | The Crown: $129.99 initial + $9.99/month
              </p>
            </>
          ) : (
            <AuthModal>
              <Button className="w-full bg-amber-600 hover:bg-amber-700">
                <Crown className="w-4 h-4 mr-2" />
                Sign In to Access Crown Features
              </Button>
            </AuthModal>
          )}
        </div>

        <div className="text-xs text-gray-500 space-y-1">
          <p>✓ Unlimited AI genealogy analysis</p>
          <p>✓ Full language restoration tools</p>
          <p>✓ Complete treaty database access</p>
          <p>✓ The Guru AI guide access</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Usage limit component
export const UsageLimitGate = ({ 
  feature, 
  currentUsage, 
  children, 
  className = "" 
}) => {
  const { getUsageLimit, isUnlimited, hasFeatureAccess } = useSubscription();
  
  const limit = getUsageLimit(feature);
  const unlimited = isUnlimited(feature);
  const hasAccess = hasFeatureAccess(feature);
  
  // If no access to feature at all
  if (!hasAccess) {
    return (
      <SubscriptionGate 
        feature={feature}
        title="Usage Limit Reached"
        description={`You've used ${currentUsage}/${limit} ${feature} this month`}
        className={className}
      />
    );
  }
  
  // If unlimited or under limit
  if (unlimited || currentUsage < limit) {
    return <>{children}</>;
  }
  
  // Usage limit reached
  return (
    <Card className={`bg-gradient-to-br from-red-50 to-pink-50 border-red-200 ${className}`}>
      <CardContent className="p-6 text-center">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-6 h-6 text-red-600" />
        </div>
        
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Usage Limit Reached
        </h3>
        
        <p className="text-sm text-gray-600 mb-4">
          You've used {currentUsage}/{limit} {feature} this month. 
          Your limit will reset next month, or upgrade for unlimited access.
        </p>
        
        <Button 
          className="bg-amber-600 hover:bg-amber-700"
          onClick={() => window.location.href = '/subscription'}
        >
          <Crown className="w-4 h-4 mr-2" />
          Upgrade for Unlimited Access
        </Button>
      </CardContent>
    </Card>
  );
};

export default SubscriptionGate;